import type { Database, SqlJsStatic } from 'sql.js'
import { isDeadObservation, normalizeDraft, normalizeRing } from '../domain'
import type {
  BirdAppearance, BirdInput, BirdSearchResult, BirdStatusOverrides, ExcelImportInfo, FamilyRecord, ManualBirdStatus, RingPrefixRule, SaveResult, SheetDetail, SheetDraft, SheetSummary, StoredPhoto
} from '../types'
import { readLocalFile, writeLocalFile } from '../storage/fileStore'
import { schema } from './schema'

const DB_PATH = 'database/palomar.sqlite3'
let SQL: SqlJsStatic
let database: Database
let opening: Promise<Database> | undefined

function migrateExcelSearchSchema(db: Database) {
  const columns = [
    ['padre_nombre', "TEXT NOT NULL DEFAULT ''"],
    ['padre_anilla', "TEXT NOT NULL DEFAULT ''"],
    ['padre_plumaje', "TEXT NOT NULL DEFAULT ''"],
    ['madre_nombre', "TEXT NOT NULL DEFAULT ''"],
    ['madre_anilla', "TEXT NOT NULL DEFAULT ''"],
    ['madre_plumaje', "TEXT NOT NULL DEFAULT ''"]
  ]
  const existing = new Set(
    db.exec('PRAGMA table_info(excel_apariciones)')[0]?.values.map(row => String(row[1])) || []
  )
  columns.forEach(([name, definition]) => {
    if (!existing.has(name)) db.run(`ALTER TABLE excel_apariciones ADD COLUMN ${name} ${definition}`)
  })
}

async function openDatabase() {
  if (database) return database
  if (opening) return opening
  opening = (async () => {
    SQL = await window.initSqlJs({ locateFile: () => '/vendor/sql-wasm.wasm' })
    const stored = await readLocalFile(DB_PATH)
    database = stored ? new SQL.Database(stored) : new SQL.Database()
    database.run(schema)
    migrateExcelSearchSchema(database)
    if (!stored) await persist()
    return database
  })()
  return opening
}

async function persist() {
  await writeLocalFile(DB_PATH, database.export())
}

function first(db: Database, sql: string, params: (string | number | null)[] = []) {
  const statement = db.prepare(sql)
  statement.bind(params)
  const value = statement.step() ? statement.getAsObject() : null
  statement.free()
  return value as Record<string, string | number | null> | null
}

function all(db: Database, sql: string, params: (string | number | null)[] = []) {
  const statement = db.prepare(sql)
  statement.bind(params)
  const values: Record<string, string | number | null>[] = []
  while (statement.step()) values.push(statement.getAsObject() as Record<string, string | number | null>)
  statement.free()
  return values
}

function reusableBirdWithRing(db: Database, ring: string) {
  return first(db, `
    SELECT p.id, p.nombre, p.plumaje, p.sexo, p.observaciones
    FROM palomos p
    WHERE p.anilla = ?
      AND (
        NOT EXISTS (SELECT 1 FROM parejas px WHERE (px.padre_id = p.id OR px.madre_id = p.id) AND px.deleted_at IS NULL)
        AND NOT EXISTS (SELECT 1 FROM pichones_hoja phx WHERE phx.palomo_id = p.id AND phx.deleted_at IS NULL)
        OR EXISTS (SELECT 1 FROM parejas px JOIN hojas_cria hx ON hx.id = px.hoja_id WHERE (px.padre_id = p.id OR px.madre_id = p.id) AND px.deleted_at IS NULL AND hx.deleted_at IS NULL)
        OR EXISTS (
          SELECT 1 FROM pichones_hoja phx JOIN hojas_cria hx ON hx.id = phx.hoja_id
          WHERE phx.palomo_id = p.id AND phx.deleted_at IS NULL AND hx.deleted_at IS NULL
            AND UPPER(COALESCE(NULLIF(phx.observaciones, ''), p.observaciones, '')) NOT LIKE '%MUERTO%'
        )
      )
    ORDER BY p.updated_at DESC
    LIMIT 1
  `, [ring])
}

function saveBird(db: Database, bird: BirdInput, warnings: string[], forceNewIdentity = false) {
  const ring = normalizeRing(bird.anilla) || null
  const now = new Date().toISOString()

  if (bird.id) {
    db.run(
      'UPDATE palomos SET anilla = ?, nombre = ?, plumaje = ?, sexo = ?, observaciones = ?, updated_at = ?, sync_status = ?, version = version + 1 WHERE id = ?',
      [ring, bird.nombre.trim(), bird.plumaje.trim(), bird.sexo || '', bird.observaciones || '', now, 'local', bird.id]
    )
    return bird.id
  }

  const found = ring && !forceNewIdentity ? reusableBirdWithRing(db, ring) : null
  if (found) {
    const oldName = String(found.nombre || '').trim()
    const newName = bird.nombre.trim()
    if (oldName && newName && oldName.localeCompare(newName, 'es', { sensitivity: 'base' }) !== 0) {
      warnings.push(`La anilla ${ring} ya estaba registrada como “${oldName}” y ahora aparece como “${newName}”.`)
    }
    db.run(
      'UPDATE palomos SET nombre = ?, plumaje = ?, sexo = ?, observaciones = ?, updated_at = ?, sync_status = ?, version = version + 1 WHERE id = ?',
      [
        newName || oldName,
        bird.plumaje.trim() || String(found.plumaje || ''),
        bird.sexo || String(found.sexo || ''),
        bird.observaciones || String(found.observaciones || ''),
        now, 'local', String(found.id)
      ]
    )
    return String(found.id)
  }

  const id = crypto.randomUUID()
  db.run(
    'INSERT INTO palomos (id, anilla, nombre, plumaje, sexo, observaciones, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?, ?)',
    [id, ring, bird.nombre.trim(), bird.plumaje.trim(), bird.sexo || '', bird.observaciones || '', now, now]
  )
  return id
}
export async function findDuplicateChickRings(draft: SheetDraft): Promise<string[]> {
  const db = await openDatabase()
  const rings = draft.pichones
    .filter(item => !isDeadObservation(item.observaciones))
    .map(item => normalizeRing(item.anilla))
    .filter(Boolean)
  const duplicates = new Set<string>()
  const counts = new Map<string, number>()

  rings.forEach(ring => {
    counts.set(ring, (counts.get(ring) || 0) + 1)
    if ((counts.get(ring) || 0) > 1) duplicates.add(ring)
  })

  for (const ring of new Set(rings)) {
    const found = first(
      db,
      `SELECT p.id
       FROM palomos p
       JOIN pichones_hoja ph ON ph.palomo_id = p.id AND ph.deleted_at IS NULL
       JOIN hojas_cria h ON h.id = ph.hoja_id AND h.deleted_at IS NULL
       WHERE p.anilla = ? AND (? IS NULL OR h.id <> ?)
         AND UPPER(COALESCE(NULLIF(ph.observaciones, ''), p.observaciones, '')) NOT LIKE '%MUERTO%'
       LIMIT 1`,
      [ring, draft.id || null, draft.id || null]
    )
    if (found) duplicates.add(ring)
  }
  return [...duplicates]
}
export async function saveSheet(input: SheetDraft): Promise<SaveResult> {
  const db = await openDatabase()
  const draft = normalizeDraft(input)
  const warnings: string[] = []
  const id = draft.id || crypto.randomUUID()
  const now = new Date().toISOString()
  let newPhoto: StoredPhoto | undefined

  if (draft.photo) {
    const extension = draft.photo.name.split('.').pop()?.replace(/[^a-z0-9]/gi, '') || 'jpg'
    const path = `images/${id}/${crypto.randomUUID()}.${extension}`
    const reference = await writeLocalFile(path, draft.photo)
    newPhoto = {
      id: crypto.randomUUID(),
      reference,
      name: draft.photo.name,
      mime: draft.photo.type || 'application/octet-stream',
      size: draft.photo.size,
      createdAt: now
    }
  }

  db.run('BEGIN')
  try {
    const padreId = saveBird(db, draft.padre, warnings)
    const madreId = saveBird(db, draft.madre, warnings)

    if (draft.id) {
      db.run(
        'UPDATE hojas_cria SET anio = ?, numero = ?, updated_at = ?, sync_status = ?, version = version + 1 WHERE id = ?',
        [draft.anio, draft.hoja, now, 'local', id]
      )
      const pareja = first(db, 'SELECT id FROM parejas WHERE hoja_id = ?', [id])
      if (pareja) {
        db.run(
          'UPDATE parejas SET padre_id = ?, madre_id = ?, updated_at = ?, sync_status = ?, version = version + 1 WHERE id = ?',
          [padreId, madreId, now, 'local', String(pareja.id)]
        )
      } else {
        db.run(
          'INSERT INTO parejas (id, hoja_id, padre_id, madre_id, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
          [crypto.randomUUID(), id, padreId, madreId, now, now]
        )
      }
      db.run(
        'UPDATE pichones_hoja SET deleted_at = ?, updated_at = ?, sync_status = ?, version = version + 1 WHERE hoja_id = ? AND deleted_at IS NULL',
        [now, now, 'local', id]
      )
    } else {
      db.run(
        'INSERT INTO hojas_cria (id, anio, numero, created_at, updated_at) VALUES (?, ?, ?, ?, ?)',
        [id, draft.anio, draft.hoja, now, now]
      )
      db.run(
        'INSERT INTO parejas (id, hoja_id, padre_id, madre_id, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?)',
        [crypto.randomUUID(), id, padreId, madreId, now, now]
      )
    }

    draft.pichones.forEach((chick, index) => {
      const persistedChick = chick.relationId ? chick : { ...chick, id: undefined }
      const birdId = saveBird(db, persistedChick, warnings, !chick.relationId && isDeadObservation(chick.observaciones))
      db.run(
        'INSERT INTO pichones_hoja (id, hoja_id, palomo_id, orden, observaciones, created_at, updated_at) VALUES (?, ?, ?, ?, ?, ?, ?)',
        [crypto.randomUUID(), id, birdId, index, chick.observaciones || '', now, now]
      )
    })

    if (newPhoto) {
      db.run(
        'UPDATE imagenes_documentos SET deleted_at = ?, sync_status = ?, version = version + 1 WHERE hoja_id = ? AND deleted_at IS NULL',
        [now, 'local', id]
      )
      db.run(
        'INSERT INTO imagenes_documentos (id, hoja_id, ruta, nombre_original, mime_type, tamano_bytes, created_at) VALUES (?, ?, ?, ?, ?, ?, ?)',
        [newPhoto.id, id, newPhoto.reference, newPhoto.name, newPhoto.mime, newPhoto.size, newPhoto.createdAt]
      )
    }

    db.run('COMMIT')
    await persist()
    return { id, warnings }
  } catch (error) {
    db.run('ROLLBACK')
    throw error
  }
}

export async function listRingPrefixes(): Promise<RingPrefixRule[]> {
  const db = await openDatabase()
  return all(db, 'SELECT anio, prefijo FROM prefijos_anilla ORDER BY anio').map(item => ({
    anio: Number(item.anio),
    prefijo: String(item.prefijo || '')
  }))
}

export async function saveRingPrefix(anio: number, prefijo: string): Promise<void> {
  const db = await openDatabase()
  const clean = prefijo.toLocaleUpperCase('es-ES').replace(/[^A-Z]/g, '').slice(0, 4)
  if (!clean) db.run('DELETE FROM prefijos_anilla WHERE anio = ?', [anio])
  else db.run(`
    INSERT INTO prefijos_anilla (anio, prefijo, updated_at) VALUES (?, ?, ?)
    ON CONFLICT(anio) DO UPDATE SET prefijo = excluded.prefijo, updated_at = excluded.updated_at,
      sync_status = 'local', version = prefijos_anilla.version + 1
  `, [anio, clean, new Date().toISOString()])
  await persist()
}

export async function replaceExcelSearchIndex(filename: string, birds: BirdSearchResult[]): Promise<ExcelImportInfo> {
  const db = await openDatabase()
  const importId = crypto.randomUUID()
  const importedAt = new Date().toISOString()
  db.run('BEGIN')
  try {
    db.run('DELETE FROM excel_apariciones')
    db.run('DELETE FROM excel_palomos')
    db.run('DELETE FROM excel_imports')
    db.run('INSERT INTO excel_imports (id, filename, imported_at) VALUES (?, ?, ?)', [importId, filename, importedAt])
    let appearanceCount = 0
    birds.forEach(bird => {
      const birdId = crypto.randomUUID()
      db.run('INSERT INTO excel_palomos (id, import_id, anilla, nombre, plumaje, sexo, observaciones) VALUES (?, ?, ?, ?, ?, ?, ?)', [birdId, importId, bird.anilla || null, bird.nombre || '', bird.plumaje || '', bird.sexo || '', bird.observaciones || ''])
      bird.appearances.forEach(appearance => {
        db.run(
          'INSERT INTO excel_apariciones (id, palomo_id, anio, hoja, rol, observaciones, padre_nombre, padre_anilla, padre_plumaje, madre_nombre, madre_anilla, madre_plumaje) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)',
          [
            crypto.randomUUID(), birdId, appearance.anio, appearance.hoja, appearance.role, appearance.observaciones || '',
            appearance.padre?.nombre || '', appearance.padre?.anilla || '', appearance.padre?.plumaje || '',
            appearance.madre?.nombre || '', appearance.madre?.anilla || '', appearance.madre?.plumaje || ''
          ]
        )
        appearanceCount += 1
      })
    })
    db.run('COMMIT')
    await persist()
    return { filename, importedAt, birds: birds.length, appearances: appearanceCount }
  } catch (error) {
    db.run('ROLLBACK')
    throw error
  }
}

export async function getExcelImportInfo(): Promise<ExcelImportInfo | null> {
  const db = await openDatabase()
  const item = first(db, `
    SELECT i.filename, i.imported_at AS importedAt,
      (SELECT COUNT(*) FROM excel_palomos p WHERE p.import_id = i.id) AS birds,
      (SELECT COUNT(*) FROM excel_apariciones a JOIN excel_palomos p ON p.id = a.palomo_id WHERE p.import_id = i.id) AS appearances
    FROM excel_imports i ORDER BY i.imported_at DESC LIMIT 1
  `)
  return item ? { filename: String(item.filename), importedAt: String(item.importedAt), birds: Number(item.birds), appearances: Number(item.appearances) } : null
}

export async function searchExcelBirds(query: string): Promise<BirdSearchResult[]> {
  const db = await openDatabase()
  const value = query.trim()
  if (!value) return []
  const textLike = `%${value.toLocaleUpperCase('es-ES')}%`
  const ringLike = `%${normalizeRing(value).replace(/[^A-Z0-9]/g, '')}%`
  const birds = all(db, `
    SELECT id, nombre, anilla, plumaje, sexo, observaciones
    FROM excel_palomos
    WHERE UPPER(COALESCE(nombre, '')) LIKE ?
      OR UPPER(COALESCE(anilla, '')) LIKE ?
      OR REPLACE(REPLACE(UPPER(COALESCE(anilla, '')), '-', ''), ' ', '') LIKE ?
      OR UPPER(COALESCE(plumaje, '')) LIKE ?
      OR UPPER(COALESCE(sexo, '')) LIKE ?
      OR UPPER(COALESCE(observaciones, '')) LIKE ?
    ORDER BY CASE WHEN UPPER(COALESCE(anilla, '')) = UPPER(?) THEN 0 ELSE 1 END, nombre, anilla
    LIMIT 100
  `, [textLike, textLike, ringLike, textLike, textLike, textLike, normalizeRing(value)])

  return birds.map(bird => ({
    id: String(bird.id),
    nombre: String(bird.nombre || ''),
    anilla: String(bird.anilla || ''),
    plumaje: String(bird.plumaje || ''),
    sexo: String(bird.sexo || ''),
    observaciones: String(bird.observaciones || ''),
    source: 'EXCEL' as const,
    appearances: all(db, `
      SELECT anio, hoja, rol AS role, observaciones, padre_nombre AS padreNombre, padre_anilla AS padreAnilla, padre_plumaje AS padrePlumaje, madre_nombre AS madreNombre, madre_anilla AS madreAnilla, madre_plumaje AS madrePlumaje
      FROM excel_apariciones WHERE palomo_id = ?
      ORDER BY anio DESC, hoja DESC
    `, [String(bird.id)]).map(item => ({
      sheetId: '',
      anio: Number(item.anio),
      hoja: Number(item.hoja),
      role: String(item.role) as BirdAppearance['role'],
      observaciones: String(item.observaciones || ''),
      deceased: isDeadObservation(String(item.observaciones || '')),
      padre: { nombre: String(item.padreNombre || ''), anilla: String(item.padreAnilla || ''), plumaje: String(item.padrePlumaje || '') },
      madre: { nombre: String(item.madreNombre || ''), anilla: String(item.madreAnilla || ''), plumaje: String(item.madrePlumaje || '') }
    }))
  }))
}

export async function searchBirds(query: string): Promise<BirdSearchResult[]> {
  const db = await openDatabase()
  const value = query.trim()
  if (!value) return []
  const textLike = `%${value.toLocaleUpperCase('es-ES')}%`
  const ringLike = `%${normalizeRing(value).replace(/[^A-Z0-9]/g, '')}%`

  const birds = all(db, `
    SELECT id, nombre, anilla, plumaje, sexo, observaciones
    FROM palomos
    WHERE deleted_at IS NULL
      AND (
        UPPER(COALESCE(nombre, '')) LIKE ?
        OR UPPER(COALESCE(anilla, '')) LIKE ?
        OR REPLACE(REPLACE(UPPER(COALESCE(anilla, '')), '-', ''), ' ', '') LIKE ?
        OR UPPER(COALESCE(plumaje, '')) LIKE ?
        OR UPPER(COALESCE(sexo, '')) LIKE ?
        OR UPPER(COALESCE(observaciones, '')) LIKE ?
      )
    ORDER BY
      CASE WHEN UPPER(COALESCE(anilla, '')) = UPPER(?) THEN 0 ELSE 1 END,
      nombre, anilla
    LIMIT 100
  `, [textLike, textLike, ringLike, textLike, textLike, textLike, normalizeRing(value)])

  return birds.map(bird => {
    const appearances = all(db, `
      SELECT h.id AS sheetId, h.anio, h.numero AS hoja, 'PADRE' AS role, '' AS observaciones,
        '' AS padreNombre, '' AS padreAnilla, '' AS padrePlumaje,
        '' AS madreNombre, '' AS madreAnilla, '' AS madrePlumaje
      FROM parejas px
      JOIN hojas_cria h ON h.id = px.hoja_id AND h.deleted_at IS NULL
      WHERE px.padre_id = ? AND px.deleted_at IS NULL
      UNION ALL
      SELECT h.id AS sheetId, h.anio, h.numero AS hoja, 'MADRE' AS role, '' AS observaciones,
        '' AS padreNombre, '' AS padreAnilla, '' AS padrePlumaje,
        '' AS madreNombre, '' AS madreAnilla, '' AS madrePlumaje
      FROM parejas px
      JOIN hojas_cria h ON h.id = px.hoja_id AND h.deleted_at IS NULL
      WHERE px.madre_id = ? AND px.deleted_at IS NULL
      UNION ALL
      SELECT h.id AS sheetId, h.anio, h.numero AS hoja, 'PICHÓN' AS role,
        COALESCE(NULLIF(ph.observaciones, ''), ?) AS observaciones,
        pp.nombre AS padreNombre, pp.anilla AS padreAnilla, pp.plumaje AS padrePlumaje,
        pm.nombre AS madreNombre, pm.anilla AS madreAnilla, pm.plumaje AS madrePlumaje
      FROM pichones_hoja ph
      JOIN hojas_cria h ON h.id = ph.hoja_id AND h.deleted_at IS NULL
      LEFT JOIN parejas px ON px.hoja_id = h.id AND px.deleted_at IS NULL
      LEFT JOIN palomos pp ON pp.id = px.padre_id
      LEFT JOIN palomos pm ON pm.id = px.madre_id
      WHERE ph.palomo_id = ? AND ph.deleted_at IS NULL
      ORDER BY anio DESC, hoja DESC
    `, [String(bird.id), String(bird.id), String(bird.observaciones || ''), String(bird.id)])

    return {
      id: String(bird.id),
      nombre: String(bird.nombre || ''),
      anilla: String(bird.anilla || ''),
      plumaje: String(bird.plumaje || ''),
      sexo: String(bird.sexo || ''),
      observaciones: String(bird.observaciones || ''),
      source: 'APP' as const,
      appearances: appearances.map(item => ({
        sheetId: String(item.sheetId),
        anio: Number(item.anio),
        hoja: Number(item.hoja),
        role: String(item.role) as BirdAppearance['role'],
        observaciones: String(item.observaciones || ''),
        deceased: isDeadObservation(String(item.observaciones || '')),
        padre: { nombre: String(item.padreNombre || ''), anilla: String(item.padreAnilla || ''), plumaje: String(item.padrePlumaje || '') },
        madre: { nombre: String(item.madreNombre || ''), anilla: String(item.madreAnilla || ''), plumaje: String(item.madrePlumaje || '') }
      }))
    }
  })
}

export async function listFamilyRecords(): Promise<FamilyRecord[]> {
  const db = await openDatabase()
  const local = all(db, `
    SELECT c.id AS birdId, h.id AS sheetId, h.anio, h.numero AS hoja,
      c.nombre, c.anilla, c.plumaje, c.sexo,
      COALESCE(NULLIF(ph.observaciones, ''), c.observaciones, '') AS observaciones,
      pp.nombre AS padreNombre, pp.anilla AS padreAnilla, pp.plumaje AS padrePlumaje,
      pm.nombre AS madreNombre, pm.anilla AS madreAnilla, pm.plumaje AS madrePlumaje
    FROM pichones_hoja ph
    JOIN palomos c ON c.id = ph.palomo_id AND c.deleted_at IS NULL
    JOIN hojas_cria h ON h.id = ph.hoja_id AND h.deleted_at IS NULL
    JOIN parejas px ON px.hoja_id = h.id AND px.deleted_at IS NULL
    JOIN palomos pp ON pp.id = px.padre_id AND pp.deleted_at IS NULL
    JOIN palomos pm ON pm.id = px.madre_id AND pm.deleted_at IS NULL
    WHERE ph.deleted_at IS NULL
  `)
  const excel = all(db, `
    SELECT p.id AS birdId, '' AS sheetId, a.anio, a.hoja,
      p.nombre, p.anilla, p.plumaje, p.sexo,
      COALESCE(NULLIF(a.observaciones, ''), p.observaciones, '') AS observaciones,
      a.padre_nombre AS padreNombre, a.padre_anilla AS padreAnilla, a.padre_plumaje AS padrePlumaje,
      a.madre_nombre AS madreNombre, a.madre_anilla AS madreAnilla, a.madre_plumaje AS madrePlumaje
    FROM excel_apariciones a
    JOIN excel_palomos p ON p.id = a.palomo_id
    WHERE a.rol = 'PICHÓN'
  `)
  const convert = (item: Record<string, string | number | null>, source: 'APP' | 'EXCEL'): FamilyRecord => ({
    birdId: String(item.birdId),
    source,
    sheetId: String(item.sheetId || ''),
    anio: Number(item.anio),
    hoja: Number(item.hoja),
    nombre: String(item.nombre || ''),
    anilla: normalizeRing(String(item.anilla || '')),
    plumaje: String(item.plumaje || ''),
    sexo: String(item.sexo || ''),
    observaciones: String(item.observaciones || ''),
    deceased: isDeadObservation(String(item.observaciones || '')),
    padre: { nombre: String(item.padreNombre || ''), anilla: normalizeRing(String(item.padreAnilla || '')), plumaje: String(item.padrePlumaje || '') },
    madre: { nombre: String(item.madreNombre || ''), anilla: normalizeRing(String(item.madreAnilla || '')), plumaje: String(item.madrePlumaje || '') }
  })
  return [...local.map(item => convert(item, 'APP')), ...excel.map(item => convert(item, 'EXCEL'))]
}

export async function listBirdsForStatusReview(): Promise<BirdSearchResult[]> {
  const records = await listFamilyRecords()
  const birds = new Map<string, BirdSearchResult>()
  records.forEach(record => {
    const key = `${record.source}|${record.birdId}`
    const current = birds.get(key) || {
      id: record.birdId,
      source: record.source,
      nombre: record.nombre,
      anilla: record.anilla,
      plumaje: record.plumaje,
      sexo: record.sexo,
      observaciones: record.observaciones,
      appearances: []
    }
    current.appearances.push({
      sheetId: record.sheetId,
      anio: record.anio,
      hoja: record.hoja,
      role: 'PICHÓN',
      observaciones: record.observaciones,
      deceased: record.deceased,
      padre: record.padre,
      madre: record.madre
    })
    birds.set(key, current)
  })
  return [...birds.values()]
}

export async function listBirdStatusOverrides(): Promise<BirdStatusOverrides> {
  const db = await openDatabase()
  return Object.fromEntries(all(db, 'SELECT identity_key, status FROM bird_status_overrides').map(item => [
    String(item.identity_key), String(item.status) as ManualBirdStatus
  ]))
}

export async function saveBirdStatusOverride(identityKey: string, status: ManualBirdStatus): Promise<void> {
  const db = await openDatabase()
  db.run(`
    INSERT INTO bird_status_overrides (identity_key, status, updated_at) VALUES (?, ?, ?)
    ON CONFLICT(identity_key) DO UPDATE SET status = excluded.status, updated_at = excluded.updated_at,
      sync_status = 'local', version = bird_status_overrides.version + 1
  `, [identityKey, status, new Date().toISOString()])
  await persist()
}

export async function listSheets(): Promise<SheetSummary[]> {
  const db = await openDatabase()
  return all(db, `
    SELECT h.id, h.anio, h.numero AS hoja, pp.nombre AS padre, pm.nombre AS madre,
      COUNT(ph.id) AS pichones, h.created_at AS createdAt,
      (SELECT i.ruta FROM imagenes_documentos i WHERE i.hoja_id = h.id AND i.deleted_at IS NULL ORDER BY i.created_at DESC LIMIT 1) AS photoReference,
      (SELECT i.mime_type FROM imagenes_documentos i WHERE i.hoja_id = h.id AND i.deleted_at IS NULL ORDER BY i.created_at DESC LIMIT 1) AS photoMime
    FROM hojas_cria h
    JOIN parejas p ON p.hoja_id = h.id
    JOIN palomos pp ON pp.id = p.padre_id
    JOIN palomos pm ON pm.id = p.madre_id
    LEFT JOIN pichones_hoja ph ON ph.hoja_id = h.id AND ph.deleted_at IS NULL
    WHERE h.deleted_at IS NULL
    GROUP BY h.id ORDER BY h.anio DESC, h.numero DESC
  `).map(row => ({
    id: String(row.id),
    anio: Number(row.anio),
    hoja: Number(row.hoja),
    padre: String(row.padre || ''),
    madre: String(row.madre || ''),
    pichones: Number(row.pichones),
    createdAt: String(row.createdAt),
    photoReference: row.photoReference ? String(row.photoReference) : undefined,
    photoMime: row.photoMime ? String(row.photoMime) : undefined
  }))
}

export async function listSheetDetails(): Promise<SheetDetail[]> {
  const summaries = await listSheets()
  const details = await Promise.all(summaries.map(sheet => getSheet(sheet.id)))
  return details.filter((sheet): sheet is SheetDetail => Boolean(sheet))
}

export async function getSheet(id: string): Promise<SheetDetail | null> {
  const db = await openDatabase()
  const sheet = first(db, `
    SELECT h.id, h.anio, h.numero AS hoja, h.created_at AS createdAt,
      pp.id AS padreId, pp.nombre AS padreNombre, pp.anilla AS padreAnilla, pp.plumaje AS padrePlumaje,
      pm.id AS madreId, pm.nombre AS madreNombre, pm.anilla AS madreAnilla, pm.plumaje AS madrePlumaje
    FROM hojas_cria h
    JOIN parejas p ON p.hoja_id = h.id AND p.deleted_at IS NULL
    JOIN palomos pp ON pp.id = p.padre_id
    JOIN palomos pm ON pm.id = p.madre_id
    WHERE h.id = ? AND h.deleted_at IS NULL
  `, [id])
  if (!sheet) return null

  const chicks = all(db, `
    SELECT c.id, ph.id AS relationId, c.anilla, c.nombre, c.plumaje, c.sexo,
      COALESCE(NULLIF(ph.observaciones, ''), c.observaciones) AS observaciones
    FROM pichones_hoja ph
    JOIN palomos c ON c.id = ph.palomo_id
    WHERE ph.hoja_id = ? AND ph.deleted_at IS NULL
    ORDER BY ph.orden
  `, [id])
  const image = first(db, `
    SELECT id, ruta, nombre_original, mime_type, tamano_bytes, created_at
    FROM imagenes_documentos
    WHERE hoja_id = ? AND deleted_at IS NULL
    ORDER BY created_at DESC LIMIT 1
  `, [id])

  return {
    id: String(sheet.id),
    anio: Number(sheet.anio),
    hoja: Number(sheet.hoja),
    createdAt: String(sheet.createdAt),
    padre: {
      id: String(sheet.padreId), nombre: String(sheet.padreNombre || ''),
      anilla: String(sheet.padreAnilla || ''), plumaje: String(sheet.padrePlumaje || '')
    },
    madre: {
      id: String(sheet.madreId), nombre: String(sheet.madreNombre || ''),
      anilla: String(sheet.madreAnilla || ''), plumaje: String(sheet.madrePlumaje || '')
    },
    pichones: chicks.map(chick => ({
      id: String(chick.id), relationId: String(chick.relationId),
      anilla: String(chick.anilla || ''), nombre: String(chick.nombre || ''),
      plumaje: String(chick.plumaje || ''), sexo: String(chick.sexo || ''),
      observaciones: String(chick.observaciones || '')
    })),
    storedPhoto: image ? {
      id: String(image.id), reference: String(image.ruta), name: String(image.nombre_original),
      mime: String(image.mime_type), size: Number(image.tamano_bytes), createdAt: String(image.created_at)
    } : undefined
  }
}
